import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

export default function SettingsPage() {
  const toast = useToast();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await adminApi.get('/admin/settings');
        if (active) setSettings(res.data);
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  function update(key, value) {
    setSettings((s) => ({ ...s, [key]: value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await adminApi.put('/admin/settings', settings);
      setSettings(res.data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader title="Settings" subtitle="System-wide configuration" />
      <div className="page-content">
        {loading ? (
          <div className="text-center p-5"><Spinner animation="border" /></div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : (
          <Form onSubmit={onSubmit}>
            <Row className="g-3">
              <Col xs={12} lg={8}>
                <Card className="mb-3">
                  <Card.Header>Organization</Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Organization name</Form.Label>
                          <Form.Control value={settings.organization_name || ''} onChange={(e) => update('organization_name', e.target.value)} data-testid="settings-org-name-input" />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Contact email</Form.Label>
                          <Form.Control type="email" value={settings.contact_email || ''} onChange={(e) => update('contact_email', e.target.value)} data-testid="settings-contact-email-input" />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Default page size</Form.Label>
                          <Form.Control type="number" min={5} max={100} value={settings.default_page_size || 20} onChange={(e) => update('default_page_size', Number(e.target.value))} data-testid="settings-page-size-input" />
                          <Form.Text>Used as the default in lists and search results.</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group className="mt-md-4 pt-md-2">
                          <Form.Check
                            type="switch"
                            id="public-api-switch"
                            label="Public API enabled"
                            checked={!!settings.public_api_enabled}
                            onChange={(e) => update('public_api_enabled', e.target.checked)}
                            data-testid="settings-public-api-switch"
                          />
                          <Form.Text>When disabled, all <code>/api/v1/*</code> endpoints will return 503 (advisory toggle).</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>Notes</Form.Label>
                          <Form.Control as="textarea" rows={3} value={settings.notes || ''} onChange={(e) => update('notes', e.target.value)} data-testid="settings-notes-input" />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} lg={4}>
                <Card className="mb-3">
                  <Card.Header>Account</Card.Header>
                  <Card.Body>
                    <div className="text-muted" style={{ fontSize: '0.78rem' }}>Signed in as</div>
                    <div style={{ fontWeight: 600 }}>{user?.full_name || 'Admin'}</div>
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>{user?.email}</div>
                    <div className="mt-2"><span className="status-badge status-reviewed">{user?.role}</span></div>
                  </Card.Body>
                </Card>
                <Card>
                  <Card.Header>Roles &amp; permissions</Card.Header>
                  <Card.Body style={{ fontSize: '0.88rem' }}>
                    <p className="mb-2"><strong>Admin</strong> — full access to candidates, tokens, settings.</p>
                    <p className="mb-0 text-muted"><strong>Campaign Manager</strong> (planned) — view/edit only assigned candidates; no token or settings access.</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button type="submit" variant="primary" disabled={saving} data-testid="settings-save-button">
                {saving ? <Spinner size="sm" animation="border" /> : 'Save settings'}
              </Button>
            </div>
          </Form>
        )}
      </div>
    </>
  );
}
